/**
 * Daily Challenge — pure data + a deterministic picker.
 *
 * One challenge is active per calendar day, selected from the catalog
 * below via a hash of `YYYY-MM-DD`. Every player on the same day sees
 * the same challenge (so the landing banner can talk about "today's
 * challenge" as a shared thing without a backend).
 *
 * Progress is local-only (localStorage), tracked from in-game HUD state
 * transitions:
 *   - kill_rival:  fires when `kills` increments AND killer's victim was a named rival
 *   - reach_mass:  fires when `mass` ≥ target during a life
 *   - chain_combo: fires when `combo` ≥ target
 *   - survive_30s: fires when player has been alive for 30 consecutive seconds
 *
 * Adding a challenge: append to CATALOG. The `eval` returns the
 * current progress out of `goal`. Returning a value ≥ goal counts as
 * completed.
 *
 * The module is import-side-effect-free + has no React dependency so
 * it's trivial to test and can be reused on the server later.
 */

export type ChallengeKind =
  | 'reach_mass'
  | 'kill_rival'
  | 'chain_combo'
  | 'survive_time';

export interface Challenge {
  id: string;
  kind: ChallengeKind;
  /** Human-readable goal label — e.g. "Reach 3,000 mass". */
  label: string;
  /** Numeric target (mass, combo size, seconds, etc. — varies by kind). */
  goal: number;
  /** Lucide-style single-char glyph for the banner. */
  glyph: string;
  /** Accent hue 0-360 for the banner tint. */
  hue: number;
}

export const CATALOG: ReadonlyArray<Challenge> = [
  { id: 'mass_3k',  kind: 'reach_mass',  label: 'Reach 3,000 mass',   goal: 3000, glyph: '◉', hue: 200 },
  { id: 'mass_5k',  kind: 'reach_mass',  label: 'Reach 5,000 mass',   goal: 5000, glyph: '◉', hue: 245 },
  { id: 'combo_5',  kind: 'chain_combo', label: 'Chain a ×5 combo',   goal: 5,    glyph: '✺', hue: 65  },
  { id: 'combo_8',  kind: 'chain_combo', label: 'Chain a ×8 combo',   goal: 8,    glyph: '✺', hue: 110 },
  { id: 'rivals_2', kind: 'kill_rival',  label: 'Kill 2 named rivals', goal: 2,   glyph: '✦', hue: 335 },
  { id: 'rivals_3', kind: 'kill_rival',  label: 'Kill 3 named rivals', goal: 3,   glyph: '✦', hue: 290 },
  { id: 'survive',  kind: 'survive_time', label: 'Survive 60 seconds', goal: 60,  glyph: '◐', hue: 155 },
];

/** Stable hash → index. Same date → same challenge for every player.
 *  Uses a small variant of FNV-1a so it's deterministic + dependency-free. */
function hashDate(dateKey: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < dateKey.length; i++) {
    h ^= dateKey.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

/** Returns today's challenge based on the current local date.
 *  Pass a fixed key in tests to make assertions deterministic. */
export function pickToday(dateKey: string = todayKey()): Challenge {
  const idx = hashDate(dateKey) % CATALOG.length;
  return CATALOG[idx]!;
}

/** ISO-ish local-date key. Same shape as gameStore.todayISO so the two
 *  modules can interop without a shared helper. */
export function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Persisted progress shape. */
export interface ChallengeProgress {
  /** The challenge id this progress is for — guards against stale data. */
  id: string;
  /** Calendar day this progress belongs to. */
  dateKey: string;
  /** Current numeric progress (mass reached, kills scored, etc.). */
  current: number;
  /** Whether the player has hit `current >= goal` at least once today. */
  completed: boolean;
  /** Wall-clock ms when the player completed (for toast timing). */
  completedAt: number | null;
}

/** Fresh progress record for a brand-new day. */
export function freshProgress(challenge: Challenge): ChallengeProgress {
  return {
    id: challenge.id,
    dateKey: todayKey(),
    current: 0,
    completed: false,
    completedAt: null,
  };
}
